import React, { useCallback, useMemo, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { TCommentType } from '@entities/comment/model/types';
import { ICommentGroup } from '@entities/message/model/types';
import { Checklist } from '@features';
import { showToast, ToastManager } from '@features';
import { Flex, Icon, Lightbox, Popup, ReactErrorFallback } from '@shared/ui';
import { useGetCommentList } from '@widgets/Comments/api/hooks';
import { getCommentsToastScope } from '@widgets/Comments/lib/helpers/getCommentsToastScope';
import {
    useClearUnreadIndicatorOnCommentsOpen,
    useCommentsLightbox,
    useDeleteMessage,
    useHandleDelete,
} from '@widgets/Comments/model';
import { CommentsMainContext } from '@widgets/Comments/model/context';
import { CommentsFooter } from '@widgets/Comments/ui/Footer';
import { CommentsHeader } from '@widgets/Comments/ui/Header';
import { CommentsMain } from '@widgets/Comments/ui/Main';

import cls from './CommentsContent.module.css';

interface ICommentsContentProps {
    positionGuid: string;
    positionName?: string;
    commentType?: TCommentType;
    withChecklist?: boolean;
    className?: string;
    onChecklistOpenChange?: (isOpen: boolean) => void;
}
export const CommentsContent = ({
    className,
    commentType,
    positionGuid = '',
    positionName = '',
    withChecklist = false,
    onChecklistOpenChange,
}: ICommentsContentProps) => {
    const [toastPortalTarget, setToastPortalTarget] = useState<HTMLDivElement | null>(null);
    const {
        currentLightboxFileId,
        lightboxFiles,
        closeAttachmentLightbox,
        openAttachmentLightbox,
    } = useCommentsLightbox();
    const { t } = useTranslation();
    const toastScope = useMemo(() => getCommentsToastScope(positionGuid), [positionGuid]);
    const { isOpenDeleteConfirmPopup, messageToDeleteId, openDeletePopup, closeDeletePopup } =
        useDeleteMessage();
    const {
        data: resData,
        isError,
        isFetching,
        isLoading,
        refetch,
    } = useGetCommentList({ commentType, positionId: positionGuid });
    const commentsGroups = (resData?.data || []) as ICommentGroup[];

    useClearUnreadIndicatorOnCommentsOpen({
        positionGuid,
        messagesData: commentsGroups,
        isMessagesFetching: isFetching,
    });

    const { handleDeleteComment } = useHandleDelete({
        commentId: messageToDeleteId,
        commentType,
        positionGuid,
        onSuccessCb: closeDeletePopup,
        onErrorCb: (errorText) => {
            showToast(errorText, { scope: toastScope, type: 'error' });
        },
        defaultErrorText: 'Sorry, unable to delete comment! Please try again',
    });
    const handleCommentsContainerRef = useCallback((node: HTMLDivElement | null) => {
        setToastPortalTarget(node);
    }, []);

    const commentsMainContextValue = useMemo(
        () => ({
            lightboxFiles,
            currentLightboxFileId,
            openAttachmentLightbox,
            closeAttachmentLightbox,
            openDeletePopup,
        }),
        [
            closeAttachmentLightbox,
            currentLightboxFileId,
            lightboxFiles,
            openAttachmentLightbox,
            openDeletePopup,
        ]
    );

    return (
        <CommentsMainContext.Provider value={commentsMainContextValue}>
            {isError && <ReactErrorFallback refetchCb={() => refetch()} />}
            <Flex
                ref={handleCommentsContainerRef}
                className={cn(cls.CommentsContainer, className)}
                direction="column"
                gap={16}
                align="start"
                fullWidth
            >
                <CommentsHeader positionName={positionName} />
                {withChecklist && (
                    <Checklist positionGuid={positionGuid} onOpenChange={onChecklistOpenChange} />
                )}
                <CommentsMain messagesData={commentsGroups} isInitialLoading={isLoading} />
                <CommentsFooter positionGuid={positionGuid} commentType={commentType} />
                {currentLightboxFileId !== null && (
                    <Lightbox
                        files={lightboxFiles}
                        onClose={closeAttachmentLightbox}
                        currentFileId={currentLightboxFileId}
                    />
                )}
                <Popup
                    isOpen={isOpenDeleteConfirmPopup}
                    type="delete"
                    title={t('Comments.DeleteItem.Confirm.text')}
                    footerClassName={cls.PopupFooter}
                    btnActionCb={async () => {
                        try {
                            await handleDeleteComment();
                        } finally {
                            closeDeletePopup();
                        }
                    }}
                    onHidePopup={closeDeletePopup}
                />
            </Flex>
            {toastPortalTarget && (
                <ToastManager
                    toastPosition="right"
                    strategy="absolute"
                    portalTarget={toastPortalTarget}
                    delay={3000}
                    scope={toastScope}
                    renderToast={({ isToastShow, positionStyles, text, type }) => (
                        <div
                            className={cn(cls.CustomToastContainer, isToastShow && cls.isToastOpen)}
                            style={positionStyles}
                        >
                            {type === 'success' && <Icon iconName="isChecked" />}
                            {type === 'error' && <Icon iconName="cross" fill="#EC3030" />}
                            <span className={cls.CustomToastContent}>{text}</span>
                        </div>
                    )}
                />
            )}
        </CommentsMainContext.Provider>
    );
};
